import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { Countries } from '@xbs/xbs-enums';

import { ClosableModalWithErrorResponseModel } from '../../../shared/components/base/closable-modal-with-error-response-model';
import {
  NotificationService,
  CompSearchService,
  TpSafeService,
  SessionService,
  CurrenciesService,
  CountriesService,
  UtilsService,
  ReportRepositoryService
} from '../../../shared/services';
import { ModalService } from '../../../shared/modules/modal';
import { FileValidators } from '../../../shared/validators/file';
import { ObjectResponse } from '../../../shared/interfaces';
import { UploadMasterFileReportVersionModalPayload, LanguageIsoInfo, CurrencyType, Currency, UploadMasterFileReportVersionPayload, SignedUrlInfo, Country } from '../../../shared/models';
import { FileConstants } from '../../../shared/constants/file';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
  templateUrl: './upload-master-file-report-version-modal.component.html',
  styleUrls: ['./upload-master-file-report-version-modal.component.scss']
})
export class UploadMasterFileReportVersionModalComponent extends ClosableModalWithErrorResponseModel<void> implements OnInit {

  data: UploadMasterFileReportVersionModalPayload;
  subscriptions: Subscription[] = [];
  uploadMasterFileReportVersionForm: FormGroup;
  languageIsos: LanguageIsoInfo[];
  currencyTypeOptions: CurrencyType[];
  shouldDisplayPageNumber: boolean;
  private reportTaxJurisdictionId: number;
  private fileToUpload: File;

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _formBuilder: FormBuilder,
    private _compSearchService: CompSearchService,
    private _countriesService: CountriesService,
    private _currenciesService: CurrenciesService,
    private _tpSafeService: TpSafeService,
    private _sessionService: SessionService,
    private _utilsService: UtilsService,
    private _reportService: ReportRepositoryService,
    private _translateService: TranslateService

  ) {
    super(notificationService, modalService);
  }

  ngOnInit(): void {
    this.uploadMasterFileReportVersionForm = this._formBuilder.group({
      file: ['', [Validators.required, FileValidators.filenameEndsWithPdf]],
      languageIso: ['', Validators.required],
      currencyType: ['', Validators.required],
    });

    this.getLatestGeneratedVersion();
  }

  getLatestGeneratedVersion(): void {
    if (this.data.report) {
      this.subscriptions.push(this._reportService.getReportInstance(this.data.report.reportId, this.data.reportInstanceId).subscribe((reportInstance) => {
        this.data.latestGeneratedVersion = reportInstance;
        this.reportTaxJurisdictionId = Countries.ByIso[this.data.latestGeneratedVersion.taxJurisdictionCode].JurisdictionId;
        this.shouldDisplayPageNumber = this.data.latestGeneratedVersion.shouldDisplayPageNumber;
        this.getLanguageIsos();
        this.setupCurrencyTypeOptions();
      }));
    }
  }

  getFileFormControl(): AbstractControl { return this.uploadMasterFileReportVersionForm.get('file'); }
  getLanguageIsoFormControl(): AbstractControl { return this.uploadMasterFileReportVersionForm.get('languageIso'); }
  getCurrencyTypeFormControl(): AbstractControl { return this.uploadMasterFileReportVersionForm.get('currencyType'); }

  onFileSelect(files: File[]): void {
    this.fileToUpload = files[0];
  }

  save(): void {
    for (const formControl of Object.values(this.uploadMasterFileReportVersionForm.controls)) {
      formControl.markAsTouched();
    }

    if (this.uploadMasterFileReportVersionForm.valid) {
      this.isSaveDisabled = true;
      this.getSignedUrlForUploadingReport();
    }
  }

  private getLanguageIsos(): void {
    const taxYear: number = this.data.mne.container.taxYear;

    this._compSearchService.getLanguages(this.reportTaxJurisdictionId, taxYear)
      .subscribe(({ data: languageIsos }: ObjectResponse<LanguageIsoInfo[]>) => {
        this.languageIsos = languageIsos;

        this.languageIsos.forEach((language) => {
          /* eslint camelcase: ['error', { allow: ['language_iso'] }] */
          language.displayName = this._translateService.instant(`internal_local_file_report.languages.${language.language_iso}`);
        });

        this.languageIsos.sort((language1, language2) => {
          return language1.displayName > language2.displayName ? 1 : -1;
        });

        this.setLanguageSelection(languageIsos);
      });
  }

  private setupCurrencyTypeOptions(): void {
    this._countriesService.show(this.reportTaxJurisdictionId).subscribe(({ data: country }: ObjectResponse<Country>) => {
      const { upeCurrency } = this.data.mne;
      const reportTaxJurisdictionCurrency: Currency = country.defaultCurrency;

      this.currencyTypeOptions = this._currenciesService.createModalCurrencyTypeOptions(upeCurrency, reportTaxJurisdictionCurrency);

      const defaultCurrency: CurrencyType = this.currencyTypeOptions.find(currency => currency.id === this.data.latestGeneratedVersion.reportCurrency.currencyId);
      this.getCurrencyTypeFormControl().setValue(defaultCurrency && defaultCurrency.id);
    });
  }

  private setLanguageSelection(languages: LanguageIsoInfo[]): void {
    const latestReportLanguageIso: string = this.data.latestGeneratedVersion.languageIso;

    if (languages && languages.length > 0) {
      const languageToUseInfo: LanguageIsoInfo = this._utilsService.findDefaultLanguage(languages, latestReportLanguageIso);

      this.getLanguageIsoFormControl().setValue(languageToUseInfo.language_iso);
    }
  }

  private getSignedUrlForUploadingReport(): void {
    const uploadReportPayload: UploadMasterFileReportVersionPayload = this.createUploadReportPayload();

    this._tpSafeService.getSignedUrlForUploadingMasterFileReportVersion(uploadReportPayload).subscribe({
      next: ({ data: signedUrlInfo }: ObjectResponse<SignedUrlInfo>) => this.saveUploadedReportToS3(signedUrlInfo.url),
      error: () => this.isSaveDisabled = false,
    });
  }

  private createUploadReportPayload(): UploadMasterFileReportVersionPayload {
    return {
      reportContext: {
        masterFileReportId: this.data.report.reportId,
        name: this.fileToUpload.name,
        languageIso: this.getLanguageIsoFormControl().value,
        currencyType: this.getCurrencyTypeFormControl().value,
        taxJurisdictionCode: this.data.latestGeneratedVersion.taxJurisdictionCode,
        mneId: this.data.mne.entityId,
        containerId: this._sessionService.getWorkingContainer().containerId,
        mimeType: this.fileToUpload.type,
        fileExtension: FileConstants.FILE_EXTENSIONS.PDF,
        shouldDisplayPageNumber: this.shouldDisplayPageNumber
      },
    };
  }

  private saveUploadedReportToS3(signedUrl: string): void {
    this._tpSafeService.saveUploadedReportToTpSafe(signedUrl, this.fileToUpload).subscribe({
      next: () => {
        this.isSaveDisabled = false;
        this.success();
        this.close();
      },
      error: () => this.isSaveDisabled = false,
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
  }

}
